import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import RCore from 'RCore';
import { connect } from 'react-redux';
import { setParticipantPitch, setParticipantCustomName, setParticipantCustomText } from '../actions';
import TextInput from './TextInput';
import SMPlayerLogo from '../../../../widgets/components/sm/views/playerLogo/SMPlayerLogo';
import SMTeamLogo from '../../../../widgets/components/sm/views/SMTeamLogo';

const Item = styled.div`
    position: relative;
    width: 140px;
    height: 150px;
    flex-shrink: 0;
    opacity: 0.8;
    box-shadow: 0 0 9px 0 rgba(0, 0, 0, 0.5);
    background-color: rgba(0, 0, 0, 0.75);
    border-style: solid;
    border-width: 2px;
    border-image-source: radial-gradient(circle at 32% 17%, #fff, #032945 50%, #02101b 85%, #fff);
    border-image-slice: 1;
    ${({ type }) => type === 'grid' && 'width: 160px; height: 210px;'};

    ::before {
        content: '?';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 120px;
        line-height: 1;
        color: #fff;
    }

    ${({ large }) =>
        large &&
        `
        width: 192px;
        height: 251px;
        padding: 2px;

        ::before {
            font-size: 160px;
        }
    `};
`;

const Participant = styled.div`
    position: relative;
    width: 140px;
    height: 150px;
    flex-shrink: 0;
    font-size: 20px;
    font-weight: 700;
    text-align: center;
    color: #fff;

    ::before {
        ${({ type }) => type === 'grid' && "content: '';"};
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: -1;
        opacity: 0.8;
        box-shadow: 0 0 9px 0 rgba(0, 0, 0, 0.5);
        background-color: rgba(0, 0, 0, 0.75);
        border-style: solid;
        border-width: 2px;
        border-image-source: radial-gradient(circle at 32% 17%, #fff, #032945 50%, #02101b 85%, #fff);
        border-image-slice: 1;
    }

    ${({ type }) =>
        type === 'grid' &&
        `
    width: 160px;
    height: 209px;
    padding: 2px;
`};

    ${({ large }) =>
        large &&
        `
    width: 192px;
    height: 251px;
    padding: 2px;
`};
`;

const Avatar = styled.div`
    position: relative;
    width: 140px;
    height: 100px;
    display: flex;
    justify-content: center;
    ${({ avatarType }) => avatarType === 'teams' && 'padding-bottom: 5px;'};
    ${({ type }) => type === 'grid' && 'width: 160px; height: 150px; padding-top: 10px;'};
    ${({ large }) => large && 'width: 192px; height: 180px;'};
`;

const TeamLogoBadge = styled.div`
    position: absolute;
    bottom: -4px;
    left: 0;
    z-index: 2;
    pointer-events: none;
    ${({ type }) => type === 'grid' && 'left: -12px;'};
`;

const dragStart = (position, participant) => event => {
    event.dataTransfer.setData('position', position);
    event.dataTransfer.setData('participant', JSON.stringify(participant));
};

const drop = (currentPosition, currentParticipant, action) => event => {
    const position = event.dataTransfer.getData('position');
    const participant =
        event.dataTransfer.getData('participant') && JSON.parse(event.dataTransfer.getData('participant'));

    if (currentPosition && participant) {
        if (position && currentParticipant && currentPosition !== position) {
            action(position, currentParticipant);
        }
        action(currentPosition, participant);
        event.dataTransfer.clearData();
    }
};

const dragEnd = event => {
    event.preventDefault();
    event.dataTransfer.clearData();
};

const getAvatar = (type, participant, lineupType, large) => {
    const imageWrapStyle = {
        height: '100px',
        display: 'block',
        overflow: 'hidden'
    };

    switch (type) {
        case 'players': {
            let logoWidth = 100;

            if (large) {
                logoWidth = 168;
                imageWrapStyle.height = '180px';
            } else if (lineupType === 'grid') {
                logoWidth = 140;
                imageWrapStyle.height = '150px';
            }

            return [
                <SMPlayerLogo
                    key="SMPlayerLogo"
                    teamId={participant.team ? participant.team.id : 0}
                    playerId={participant.id}
                    seasonId="current"
                    isGK={participant.position === 'keeper'}
                    size="large"
                    imageWrapStyle={imageWrapStyle}
                    width={logoWidth}
                    number={participant.shirtNumber}
                />,
                <TeamLogoBadge key="TeamLogoBadge" type={lineupType} large={large}>
                    <SMTeamLogo teamId={participant.team ? participant.team.id : 0} width={(large && 45) || 35} />
                </TeamLogoBadge>
            ];
        }
        case 'teams':
            return <SMTeamLogo teamId={participant.id} width={large ? 160 : lineupType === 'grid' ? 130 : 95} />;
    }
};

function ItemPitch({
    position = '',
    participant,
    lineupType,
    lineupTemplate,
    setParticipantPitch,
    setParticipantCustomName,
    setParticipantCustomText
}) {
    const large = !!(lineupType === 'grid' && lineupTemplate === '3');

    if (!participant) {
        return (
            <Item
                onDragOver={event => event.preventDefault()}
                onDrop={drop(position, participant, setParticipantPitch)}
                large={large}
                type={lineupType}
            />
        );
    }

    const avatarType = participant.countryId ? 'teams' : 'players';

    return (
        <Participant
            onDragStart={dragStart(position, participant)}
            onDrop={drop(position, participant, setParticipantPitch)}
            onDragOver={event => event.preventDefault()}
            onDragEnd={dragEnd}
            large={large}
            type={lineupType}
        >
            <Avatar type={lineupType} large={large} avatarType={avatarType}>
                {getAvatar(participant.countryId ? 'teams' : 'players', participant, lineupType, large)}
            </Avatar>
            <TextInput
                id={participant.id}
                text={RCore.getFirstLastName(participant.name).lastName}
                type="top"
                action={setParticipantCustomName}
                large={large}
                lineupType={lineupType}
            />
            <TextInput
                id={participant.id}
                text={participant.customText}
                type="bottom"
                action={setParticipantCustomText}
                large={large}
                lineupType={lineupType}
            />
        </Participant>
    );
}

ItemPitch.propTypes = {
    position: PropTypes.string.isRequired,
    // From connect
    participant: PropTypes.object,
    lineupType: PropTypes.string,
    lineupTemplate: PropTypes.string,
    setParticipantPitch: PropTypes.func.isRequired,
    setParticipantCustomName: PropTypes.func.isRequired,
    setParticipantCustomText: PropTypes.func.isRequired
};

export default connect(
    ({ makerToolPage: { activeParticipants, mapPitchPos, lineupType, lineupTemplate } = {} }, { position = 0 }) => ({
        lineupType,
        lineupTemplate,
        participant: activeParticipants[mapPitchPos[position]]
    }),
    { setParticipantPitch, setParticipantCustomName, setParticipantCustomText }
)(ItemPitch);



/** WEBPACK FOOTER **
 ** ../src/dash/front/grid-maker-tool-page/components/ItemPitch.js
 **/